import { useMemo, useRef, useState } from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Box, Typography } from "@mui/material";
import Autocomplete from "@components/Autocomplete/Autocomplete";
import type { AutocompleteOption } from "@components/Autocomplete/Autocomplete.types";
import Button from "@components/Button/Button";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import { useNotification } from "@context/NotificationContext";
import { formatDateTime } from "@features/vessel-call/grid/utils";
import {
  mapPilotStatusToOptions,
  type MovementError,
  type PilotStationType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import {
  useUpdateMovement,
  type UpdateMovementVariables,
} from "@features/vessel-call/vessel-call-status/MovementServices/hooks/useUpdateMovement";
import { useFormattedReference } from "@hooks/useFormattedReference";
import type { Movement, MovementUpdatePayload } from "@services/movement-services/types";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_FIELD_LABEL_KEYS,
  type MovementFieldKey,
  type UpdateMovementConfig,
} from "../form/updateMovement/utils";

const LANG_BASE = "vessel-call.movement-services.update-form";

interface QuickCaptureTimePickersProps {
  movement: Movement;
  config: UpdateMovementConfig;
  canCapture: boolean;
}

type PendingQuickCapture = {
  fieldKey: MovementFieldKey;
  value?: string;
};

type QuickCaptureTimeValueKey =
  | "atc"
  | "ats"
  | "anchorAweighTime"
  | "anchorTime"
  | "pilotUnderwayTime"
  | "pobTime"
  | "firstLineAshoreTime"
  | "allFastTime"
  | "pilotDisembarkTime"
  | "allClearTime";

const TIME_VALUE_KEYS: Partial<Record<MovementFieldKey, QuickCaptureTimeValueKey>> = {
  atc: "atc",
  ats: "ats",
  anchorAweighTime: "anchorAweighTime",
  anchorTime: "anchorTime",
  pilotUnderway: "pilotUnderwayTime",
  pob: "pobTime",
  firstLineAshore: "firstLineAshoreTime",
  allFast: "allFastTime",
  pilotDisembark: "pilotDisembarkTime",
  allClear: "allClearTime",
};

const formatQuickCaptureValue = (value: string | Date | null | undefined) =>
  value
    ? (formatDateTime(value instanceof Date ? value.toISOString() : value, "-")?.replace(
        "\n",
        " ",
      ) ?? "-")
    : "-";

const QuickCaptureTimePickers: React.FC<QuickCaptureTimePickersProps> = ({
  movement,
  config,
  canCapture,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const [pendingQuickCapture, setPendingQuickCapture] = useState<PendingQuickCapture | null>(null);
  const [pilotStationSelection, setPilotStationSelection] = useState<AutocompleteOption | null>(
    null,
  );
  const [isPilotStationCleared, setIsPilotStationCleared] = useState(false);
  const [updatingFieldKey, setUpdatingFieldKey] = useState<MovementFieldKey | null>(null);
  const isQuickCaptureUpdatingRef = useRef(false);
  const { options: stations } = useFormattedReference<PilotStationType>(
    REFERENCE_DATA_TYPES.PILOT_STATIONS,
    "bff-vessel-call",
  );
  const pilotStationOptions = useMemo(() => mapPilotStatusToOptions(stations), [stations]);
  const selectedPilotStation = pilotStationOptions.find(
    (option) => option.value === movement.pilotStationCode,
  );
  const invalidateCurrentMovement = () => {
    queryClient.invalidateQueries({ queryKey: ["getCurrentMovementService"] });
  };
  const refreshCurrentMovementAfterUpdate = () => {
    setTimeout(invalidateCurrentMovement, 2000);
  };
  const updateCurrentMovementCache = (variables: UpdateMovementVariables) => {
    const { status: _status, ...movementUpdates } = variables.payload;

    queryClient.setQueriesData<Movement[]>(
      { queryKey: ["getCurrentMovementService"] },
      (currentMovements) =>
        currentMovements?.map((currentMovement) =>
          currentMovement.id === variables.id
            ? { ...currentMovement, ...movementUpdates }
            : currentMovement,
        ),
    );
  };
  const handleUpdateSuccess = (variables: UpdateMovementVariables) => {
    isQuickCaptureUpdatingRef.current = false;
    updateCurrentMovementCache(variables);
    refreshCurrentMovementAfterUpdate();
  };
  const handleUpdateError = (error: MovementError, _variables: UpdateMovementVariables) => {
    isQuickCaptureUpdatingRef.current = false;
    invalidateCurrentMovement();
    notify({
      title: t("vessel-call.movement-services.update-form.movement-updated-error-title"),
      description:
        error?.response?.data?.detail ??
        error?.message ??
        t("vessel-call.movement-services.update-form.movement-updated-error-description"),
      type: "error",
    });
  };
  const { updateMovement, isPending: isQuickCaptureUpdating } = useUpdateMovement({
    handleSuccess: handleUpdateSuccess,
    handleError: handleUpdateError,
  });

  const hasTimestamp = (fieldKey: MovementFieldKey) => {
    const valueKey = TIME_VALUE_KEYS[fieldKey];
    return valueKey ? Boolean(movement[valueKey]) : false;
  };

  const hasQuickCaptureValue = (fieldKey: MovementFieldKey) =>
    fieldKey === "pilotStation" ? Boolean(movement.pilotStationCode) : hasTimestamp(fieldKey);

  const canCaptureTime = (fieldIndex: number) => {
    const fieldKey = config.visibleQuickCaptureFields[fieldIndex];
    return (
      Boolean(TIME_VALUE_KEYS[fieldKey]) &&
      !config.visibleQuickCaptureFields.slice(fieldIndex + 1).some(hasQuickCaptureValue)
    );
  };

  const isQuickCaptureFieldActive = (fieldIndex: number) =>
    !config.visibleQuickCaptureFields.slice(fieldIndex + 1).some(hasQuickCaptureValue);

  const isPilotStationUnavailable =
    !canCapture ||
    config.visibleQuickCaptureFields
      .slice(config.visibleQuickCaptureFields.indexOf("pilotStation") + 1)
      .some(hasQuickCaptureValue);

  const captureQuickCaptureField = ({ fieldKey, value }: PendingQuickCapture) => {
    if (isQuickCaptureUpdatingRef.current) return;

    if (fieldKey === "pilotStation") {
      if (!value) return;

      isQuickCaptureUpdatingRef.current = true;
      setUpdatingFieldKey(fieldKey);
      updateMovement({
        id: movement.id,
        service: "bff-vessel-call",
        payload: { status: "UPDATED", pilotStationCode: value },
      });
      return;
    }

    const valueKey = TIME_VALUE_KEYS[fieldKey];
    if (!valueKey) return;

    isQuickCaptureUpdatingRef.current = true;
    setUpdatingFieldKey(fieldKey);
    const payload: MovementUpdatePayload = {
      status: "UPDATED",
      [valueKey]: new Date().toISOString(),
    };

    updateMovement({
      id: movement.id,
      service: "bff-vessel-call",
      payload,
    });
  };

  const requestQuickCapture = (pendingCapture: PendingQuickCapture, fieldIndex: number) => {
    if (isQuickCaptureUpdatingRef.current) return;

    const previousQuickCapture = config.visibleQuickCaptureFields
      .slice(0, fieldIndex)
      .reverse()
      .map((previousFieldKey, reverseIndex) => ({
        fieldKey: previousFieldKey,
        index: fieldIndex - reverseIndex - 1,
      }))
      .find(({ fieldKey }) => Boolean(TIME_VALUE_KEYS[fieldKey]) || fieldKey === "pilotStation");

    if (
      previousQuickCapture &&
      !hasQuickCaptureValue(previousQuickCapture.fieldKey) &&
      isQuickCaptureFieldActive(previousQuickCapture.index)
    ) {
      setPendingQuickCapture(pendingCapture);
      return;
    }

    captureQuickCaptureField(pendingCapture);
  };

  const confirmSkippedTimestamps = () => {
    if (pendingQuickCapture) {
      captureQuickCaptureField(pendingQuickCapture);
    }
    setPendingQuickCapture(null);
  };

  const discardPendingQuickCapture = () => {
    if (pendingQuickCapture?.fieldKey === "pilotStation") {
      setIsPilotStationCleared(true);
      setPilotStationSelection(null);
    }
    setPendingQuickCapture(null);
  };

  const handlePilotStationChange = (option: AutocompleteOption | null, fieldIndex: number) => {
    if (option) {
      setIsPilotStationCleared(false);
      setPilotStationSelection(option);
      requestQuickCapture({ fieldKey: "pilotStation", value: String(option.value) }, fieldIndex);
    }
  };

  return (
    <Box
      data-testid="quick-capture-time-pickers"
      display="flex"
      flexWrap="wrap"
      gap={4}
      mt={4}
      mb={4}
    >
      {config.visibleQuickCaptureFields.map((fieldKey, fieldIndex) => {
        const label = t(config.labelKeys?.[fieldKey] ?? DEFAULT_FIELD_LABEL_KEYS[fieldKey]);

        if (fieldKey === "pilotStation") {
          const displayedPilotStation = isPilotStationCleared
            ? null
            : (pilotStationSelection ?? selectedPilotStation);

          return (
            <Box key={fieldKey} display="flex" alignItems="end" gap={2} flex="0 0 auto">
              <Box minWidth={0}>
                <CustomFormLabel sx={{ fontSize: 14, whiteSpace: "nowrap" }}>
                  {label}
                </CustomFormLabel>
                {isPilotStationUnavailable ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: 14,
                      fontWeight: "fontWeightBold",
                      minWidth: 0,
                      height: 36,
                      lineHeight: "36px",
                    }}
                  >
                    {displayedPilotStation?.label ?? "-"}
                  </Typography>
                ) : (
                  <Box sx={{ width: 200 }}>
                    <Autocomplete
                      options={pilotStationOptions}
                      value={displayedPilotStation}
                      onChange={(option) => handlePilotStationChange(option, fieldIndex)}
                      placeholder={label}
                      testId="quick-capture-pilot-station"
                      disableClearable
                      disabled={isQuickCaptureUpdating}
                      inputHeight={36}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          );
        }

        const valueKey = TIME_VALUE_KEYS[fieldKey];
        const value = valueKey ? movement[valueKey] : null;
        const formattedValue = formatQuickCaptureValue(value as string | Date | null | undefined);

        return (
          <Box key={fieldKey} display="flex" alignItems="end" gap={2} flex="0 0 auto">
            <Box minWidth={0}>
              <CustomFormLabel sx={{ fontSize: 14, whiteSpace: "nowrap" }}>{label}</CustomFormLabel>
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  fontWeight: "fontWeightBold",
                  minWidth: 0,
                  height: 36,
                  lineHeight: "36px",
                }}
              >
                {formattedValue}
              </Typography>
            </Box>
            {canCapture && canCaptureTime(fieldIndex) && (
              <Button
                aria-label={`Set ${label} to the current time`}
                data-testid={`${fieldKey}-quick-capture-time-button`}
                label={<AccessTimeIcon fontSize="small" />}
                onClick={() => requestQuickCapture({ fieldKey }, fieldIndex)}
                disabled={isQuickCaptureUpdating}
                loading={isQuickCaptureUpdating && updatingFieldKey === fieldKey}
                size="small"
                variant="outlined"
                sx={{ minWidth: 36, width: 36, height: 36, p: 0, ml: "auto", flexShrink: 0 }}
              />
            )}
          </Box>
        );
      })}
      <GenericDialog
        open={pendingQuickCapture !== null}
        content={t(`${LANG_BASE}.previousTimestampsSkippedMessage`)}
        cancelButtonText={t("common.no")}
        confirmButtonText={t("common.yes")}
        handleCloseDialog={discardPendingQuickCapture}
        confirmAction={confirmSkippedTimestamps}
      />
    </Box>
  );
};

export default QuickCaptureTimePickers;
